import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import RabbiSpecialRecordingUI from '../components/RabbiSpecialRecordingUI';
import { useTraditionRabbi } from '../utils/rabbiesFirestore';
import { getRabbiSpecialAudioUrl, getRabbiSpecialAlignmentJsonUrl } from '../components/utils';
import birchotHatorahData from '../../data/birchotHatorah.json';

/* ─── theme ─────────────────────────────────────────────────────────────── */
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';
const BLUE_ACCENT = '#3B7BF4';
const KARAOKE_COLOR = '#E53E3E'; // same red used in PracticePage

/* ─── data split ─────────────────────────────────────────────────────────── */
const beforeSections = birchotHatorahData.sections.filter((s) => s.id <= 5);
const afterSections = birchotHatorahData.sections.filter((s) => s.id >= 7);

/* ─── alignment helpers (ported 1-for-1 from PracticePage) ──────────────── */
type AlignmentEntry = { start: number; end: number; word?: string; text?: string };

function sanitizeAlignment(raw: any[]): AlignmentEntry[] {
  const mapped = raw
    .map((w) => {
      const rs = typeof w.start === 'string' ? parseFloat(w.start) : w.start;
      const re = typeof w.end === 'string' ? parseFloat(w.end) : w.end;
      return {
        start: typeof rs === 'number' && !isNaN(rs) ? rs : NaN,
        end: typeof re === 'number' && !isNaN(re) ? re : NaN,
        text: w.text,
        word: w.word,
      };
    })
    .filter((w) => !(isNaN(w.start) && isNaN(w.end)));

  if (mapped.length === 0) return [];

  if (!mapped.some((w) => !isNaN(w.start))) {
    mapped.sort((a, b) => (isNaN(a.end) ? 1 : isNaN(b.end) ? -1 : a.end - b.end));
    let prevEnd = 0;
    for (const w of mapped) {
      if (isNaN(w.end)) {
        w.start = prevEnd;
        w.end = prevEnd + 0.4;
      } else {
        w.start = prevEnd;
        if (w.end <= w.start) w.end = w.start + 0.25;
        prevEnd = w.end;
      }
    }
  }

  for (let i = 0; i < mapped.length; i++) {
    if (isNaN(mapped[i].start)) {
      let pv = 0;
      for (let j = i - 1; j >= 0; j--) {
        if (!isNaN(mapped[j].end)) {
          pv = mapped[j].end;
          break;
        }
      }
      mapped[i].start = pv;
    }
    if (isNaN(mapped[i].end)) mapped[i].end = mapped[i].start + 0.3;
    if (mapped[i].start < 0) mapped[i].start = 0;
    if (mapped[i].end < mapped[i].start) mapped[i].end = mapped[i].start + 0.15;
  }

  mapped.sort((a, b) => a.start - b.start);
  let lastEnd = 0;
  for (const w of mapped) {
    if (w.end < w.start + 0.05) w.end = w.start + 0.05;
    if (w.start < lastEnd) w.start = lastEnd;
    if (w.end < w.start) w.end = w.start + 0.05;
    lastEnd = w.end;
  }
  return mapped;
}

function getActiveWordIndex(alignment: AlignmentEntry[], t: number): number {
  if (!alignment.length) return -1;
  if (t <= alignment[0].start + 0.01) return 0;
  for (let i = 0; i < alignment.length; i++) {
    if (t >= alignment[i].start && t < alignment[i].end) return i;
  }
  if (t >= alignment[alignment.length - 1].end) return alignment.length - 1;
  return -1;
}

/* ─── SectionRow ─────────────────────────────────────────────────────────── */
type SectionItem = (typeof birchotHatorahData.sections)[number];

interface SectionRowProps {
  section: SectionItem;
  isHebrew: boolean;
  /** global word index at the start of this section within the card */
  wordOffset: number;
  /** currently highlighted global word index; -1 = none */
  activeWordIndex: number;
  /** true when alignment data is loaded and we render word-by-word spans */
  karaokeReady: boolean;
}

const SectionRow: React.FC<SectionRowProps> = ({
  section,
  isHebrew,
  wordOffset,
  activeWordIndex,
  karaokeReady,
}) => {
  const isInstruction = section.type === 'instruction';
  const words = section.hebrew.replace(/־/g, ' ').split(/\s+/).filter(Boolean);

  const textStyle: React.CSSProperties = {
    fontFamily: isInstruction
      ? "var(--kol-font-sans, 'Inter', system-ui, sans-serif)"
      : '"Noto Serif Hebrew", "David Libre", serif',
    fontSize: isInstruction ? 13 : 18,
    lineHeight: 1.6,
    color: isInstruction ? TEXT_SECONDARY : TEXT_DARK,
    marginBottom: isHebrew ? 0 : 8,
    fontWeight: isInstruction ? 500 : 400,
  };

  return (
    <div
      style={{
        background: isInstruction ? 'transparent' : '#f9fafb',
        borderRadius: 12,
        padding: isInstruction ? '4px 0' : '14px 16px',
        border: isInstruction ? 'none' : `1px solid ${BORDER_LIGHT}`,
      }}
    >
      <div dir="rtl" style={textStyle}>
        {karaokeReady
          ? words.map((word, wIdx) => {
              const globalIdx = wordOffset + wIdx;
              const highlighted = globalIdx === activeWordIndex;
              return (
                <span
                  key={wIdx}
                  style={{
                    color: highlighted ? KARAOKE_COLOR : undefined,
                    fontWeight: highlighted ? 700 : undefined,
                    transition: 'color 0.12s linear',
                  }}
                >
                  {word}{' '}
                </span>
              );
            })
          : section.hebrew}
      </div>

      {!isHebrew && section.translation && (
        <div style={{ fontSize: isInstruction ? 12 : 14, color: TEXT_SECONDARY, lineHeight: 1.4 }}>
          {section.translation}
        </div>
      )}
    </div>
  );
};

/* ─── KaraokeCard ─────────────────────────────────────────────────────────── */
interface KaraokeCardProps {
  sections: SectionItem[];
  audioUrl: string | null;
  alignmentUrl: string | null;
  playLabel: string;
  pauseLabel: string;
  isHebrew: boolean;
}

const KaraokeCard: React.FC<KaraokeCardProps> = ({
  sections,
  audioUrl,
  alignmentUrl,
  playLabel,
  pauseLabel,
  isHebrew,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const alignmentRef = useRef<AlignmentEntry[]>([]);
  const [playing, setPlaying] = useState(false);
  const [alignment, setAlignment] = useState<AlignmentEntry[]>([]);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);

  // Keep ref in sync so timeupdate handler always reads latest alignment
  alignmentRef.current = alignment;

  // Fetch alignment JSON whenever the URL changes
  useEffect(() => {
    setAlignment([]);
    setActiveWordIndex(-1);
    if (!alignmentUrl) return;
    let cancelled = false;
    fetch(alignmentUrl)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setAlignment(sanitizeAlignment(Array.isArray(data) ? data : []));
      })
      .catch(() => {
        if (!cancelled) setAlignment([]);
      });
    return () => {
      cancelled = true;
    };
  }, [alignmentUrl]);

  /* play / pause */
  const handlePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      if (audio.ended || audio.currentTime >= audio.duration) audio.currentTime = 0;
      if (alignmentRef.current.length) setActiveWordIndex(0);
      try {
        await audio.play();
      } catch (e) {
        console.error('BirchotHatorah: audio play failed', audioUrl, e);
        setPlaying(false);
      }
    }
  };

  /* timeupdate — reads alignment via ref so handler never stale-closes */
  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio || !alignmentRef.current.length) return;
    setActiveWordIndex(getActiveWordIndex(alignmentRef.current, audio.currentTime));
  };

  /* cumulative word offsets per section */
  let runningOffset = 0;
  const wordOffsets = sections.map((s) => {
    const off = runningOffset;
    runningOffset += s.hebrew.replace(/־/g, ' ').split(/\s+/).filter(Boolean).length;
    return off;
  });

  const karaokeReady = alignment.length > 0;

  return (
    <section
      style={{
        background: '#ffffff',
        borderRadius: 20,
        padding: '18px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
        border: `1px solid ${BORDER_LIGHT}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        direction: 'rtl',
        marginBottom: 18,
      }}
    >
      {/* hidden audio element */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setActiveWordIndex(-1);
          }}
          onError={() => setPlaying(false)}
          onTimeUpdate={handleTimeUpdate}
          style={{ display: 'none' }}
        />
      )}

      {/* play / pause button — sits BEFORE the text */}
      {audioUrl && (
        <button
          type="button"
          onClick={handlePlayPause}
          style={{
            alignSelf: 'flex-start',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 18px',
            borderRadius: 14,
            background: BLUE_ACCENT,
            color: 'white',
            border: 'none',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(59,123,244,0.25)',
          }}
        >
          {playing ? (
            <>
              <span>⏸</span>
              {pauseLabel}
            </>
          ) : (
            <>
              <span>▶</span>
              {playLabel}
              {karaokeReady ? ' 🎵' : ''}
            </>
          )}
        </button>
      )}

      {/* text sections with per-word karaoke spans */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sections.map((section, sIdx) => (
          <SectionRow
            key={section.id}
            section={section}
            isHebrew={isHebrew}
            wordOffset={wordOffsets[sIdx]}
            activeWordIndex={activeWordIndex}
            karaokeReady={karaokeReady}
          />
        ))}
      </div>
    </section>
  );
};

/* ─── Page ───────────────────────────────────────────────────────────────── */
const BirchotHatorahPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language?.startsWith('he');
  const rabbi = useTraditionRabbi('parasha');
  const isRabbi = typeof localStorage !== 'undefined' && !!localStorage.getItem('rabbiToken');

  const nusach = rabbi?.tradition?.toLowerCase() || 'ashkenaz';
  const rabbiKey = rabbi?.key || rabbi?.id || 'gal_levy';

  const beforeAudioUrl = rabbi
    ? getRabbiSpecialAudioUrl('birchot_hatorah_before', nusach, rabbiKey)
    : null;
  const afterAudioUrl = rabbi
    ? getRabbiSpecialAudioUrl('birchot_hatorah_after', nusach, rabbiKey)
    : null;
  const beforeAlignmentUrl = rabbi
    ? getRabbiSpecialAlignmentJsonUrl('birchot_hatorah_before', nusach, rabbiKey)
    : null;
  const afterAlignmentUrl = rabbi
    ? getRabbiSpecialAlignmentJsonUrl('birchot_hatorah_after', nusach, rabbiKey)
    : null;

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: '#f9fafb',
        fontFamily: "var(--kol-font-sans, 'Inter', system-ui, sans-serif)",
        color: TEXT_DARK,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '18px 18px 110px',
          boxSizing: 'border-box',
          direction: 'rtl',
          flexGrow: 1,
          overflowY: 'auto',
        }}
      >
        {/* Rabbi recording panel */}
        {isRabbi && (
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 16,
              marginBottom: 18,
              border: `1px solid ${BORDER_LIGHT}`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            }}
          >
            <div
              style={{ fontSize: 16, fontWeight: 700, color: TEXT_DARK, marginBottom: 12 }}
              dir="rtl"
            >
              {t('rabbi_upload_record_here', 'Record here (see text below)')}
            </div>
            <RabbiSpecialRecordingUI type="birchot_hatorah" />
          </div>
        )}

        {/* Page title */}
        <h3
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: TEXT_DARK,
            margin: '0 0 18px',
            direction: 'rtl',
          }}
        >
          {t(
            'user_portal_birchot_hatorah_section',
            isHebrew ? birchotHatorahData.titleHe : birchotHatorahData.titleEn,
          )}
        </h3>

        {/* ── Before kriya ── */}
        <KaraokeCard
          sections={beforeSections}
          audioUrl={beforeAudioUrl}
          alignmentUrl={beforeAlignmentUrl}
          playLabel={t('play_before_kriya', 'השמע לפני הקריאה')}
          pauseLabel={t('pause', 'השהה')}
          isHebrew={isHebrew}
        />

        {/* ── After kriya ── */}
        <KaraokeCard
          sections={afterSections}
          audioUrl={afterAudioUrl}
          alignmentUrl={afterAlignmentUrl}
          playLabel={t('play_after_kriya', 'השמע אחרי הקריאה')}
          pauseLabel={t('pause', 'השהה')}
          isHebrew={isHebrew}
        />
      </div>

      <BottomNav />
    </div>
  );
};

export default BirchotHatorahPage;
